import axios from "axios";
import { navigate } from './Navigator';
import {
  LOCAL_SESSION_ID,
  LOCAL_USER_ID,
  LOCAL_ROLES,
  LOCAL_AUTH_PROVIDER,
  LOCAL_SSO_APP_ID,
} from "./Constants";

import { createContext } from 'preact';
import { useEffect, useState, useRef, useCallback } from 'preact/hooks';

/* ============================================================
   Interceptor global de axios: inyecta Bearer token
   desde localStorage en CADA request que no traiga
   Authorization explícito.
   ============================================================ */
if (typeof window !== "undefined") {
  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(LOCAL_SESSION_ID);
      if (token && !config.headers?.Authorization) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
}

export function bootAxiosSessionHeader() {
  // Legacy no-op: interceptor global ya lo hace todo
}

export function persistSession(
  token,
  userId,
  roles,
  extra = {}
) {
  if (token) {
    localStorage.setItem(LOCAL_SESSION_ID, token);
  }
  if (userId) {
    localStorage.setItem(LOCAL_USER_ID, userId);
  }
  if (roles) {
    localStorage.setItem(LOCAL_ROLES, JSON.stringify(roles));
  }
  if (extra.authProvider) {
    localStorage.setItem(LOCAL_AUTH_PROVIDER, extra.authProvider);
  }
  if (extra.ssoAppId) {
    localStorage.setItem(LOCAL_SSO_APP_ID, extra.ssoAppId);
  }
}

export function clearSession() {
  localStorage.removeItem(LOCAL_SESSION_ID);
  localStorage.removeItem(LOCAL_USER_ID);
  localStorage.removeItem(LOCAL_ROLES);
  localStorage.removeItem(LOCAL_AUTH_PROVIDER);
  localStorage.removeItem(LOCAL_SSO_APP_ID);
}

export function getStoredSessionMeta() {
  if (typeof window === 'undefined') {
    return { token: '', authProvider: 'sso', roles: [] };
  }

  try {
    const token = localStorage.getItem(LOCAL_SESSION_ID) || '';
    const authProvider = localStorage.getItem(LOCAL_AUTH_PROVIDER) || 'sso';
    const rawRoles = localStorage.getItem(LOCAL_ROLES);
    const roles = rawRoles ? JSON.parse(rawRoles) : [];
    return { token, authProvider, roles: Array.isArray(roles) ? roles : [] };
  } catch {
    return { token: '', authProvider: 'sso', roles: [] };
  }
}

export function normalizeSessionResponse(data) {
  if (!data?.ok) {
    return { ok: false };
  }

   const normalizedRoles = Array.isArray(data.roles) ? data.roles : [];
   const normalizedType = data.type
     ? data.type
     : data.activeRole && String(data.activeRole).includes('::')
       ? String(data.activeRole).split('::').pop()
       : data.role && String(data.role).includes('::')
         ? String(data.role).split('::').pop()
         : data.role || undefined;

  return {
    ok: true,
    token: data.token || data.sessionId,
    userId: data.userId,
    type: normalizedType,
    roles: normalizedRoles,
    authProvider: data.authProvider || 'sso',
    createdAt: data.createdAt,
    expiresAt: data.expiresAt,
    user: data.user
      ? {
          id: data.user.id,
          name: data.user.name || data.user.email?.split("@")[0] || "Usuario",
          email: data.user.email,
          phone: data.user.phone,
          status: data.user.status,
        }
      : undefined,
    menu: data.menu ? data.menu : [],
  };
}

const isDevEnvironment = !import.meta.env.PROD;

export const useUserSession = (options = {}) => {
  const { sessionEndpointUrl, urlLogout, userExtractor = null } = options;

  const isProd = import.meta.env.PROD;

  const finalSessionEndpointUrl =
    sessionEndpointUrl || (isProd
      ? `/auth/session`
      : `http://localhost:3000/auth/session`);

  const finalUrlLogout =
    urlLogout || (isProd
      ? `/auth/session/logout?redirect=/adevs-admin/login`
      : `http://localhost:3000/auth/session/logout?redirect=/adevs-admin/login`);

  const [user, setUser] = useState(null);
  const [raw, setRaw] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refs para control de vida y reintentos
  const isMountedRef = useRef(true);
  const retryRef = useRef(0);
  const maxRetries = 3;

  const fetchSession = useCallback(async () => {
    try {
      if(import.meta.env.DEV){
        console.info("[SessionContext]", {
          finalSessionEndpointUrl, finalUrlLogout, VITE_DEV: import.meta.env.VITE_DEV,isDevEnvironment,sessionEndpointUrl,urlLogout
        })
      }
      setIsLoading(true);
      setError(null);

      const token = localStorage.getItem(LOCAL_SESSION_ID);

      const res = await axios.get(finalSessionEndpointUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        validateStatus: () => true,
      });

      if (!isMountedRef.current) return;

      const norm = normalizeSessionResponse(res.data);
      setRaw(norm);

      if (norm.ok) {
        const userData =
          typeof userExtractor === "function" ? userExtractor(res.data) : norm.user || null;
        setUser(userData);

        if (norm.token) {
          localStorage.setItem(LOCAL_SESSION_ID, norm.token);
        }
        if (norm.authProvider) {
          localStorage.setItem(LOCAL_AUTH_PROVIDER, norm.authProvider);
        }
        if (Array.isArray(norm.roles)) {
          localStorage.setItem(LOCAL_ROLES, JSON.stringify(norm.roles));
        }
        if (norm.userId) {
          localStorage.setItem(LOCAL_USER_ID, String(norm.userId));
        }
      } else {
        setUser(null);
        clearSession();
      }
      // éxito -> reinicia contador
      retryRef.current = 0;
    } catch (err) {
      if (!isMountedRef.current) return;

      // Reintentos solo para errores de red (sin response)
      if (retryRef.current < maxRetries && axios.isAxiosError(err) && !err.response) {
        retryRef.current += 1;
        setTimeout(() => { if (isMountedRef.current) fetchSession(); }, 1000 * retryRef.current);
        return;
      }
      setError(err);
      setUser(null);
      clearSession();
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [finalSessionEndpointUrl, userExtractor]);

  // Extraer token de URL si viene de SSO callback (?token=...)
  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const tokenFromUrl = params.get("token");
      if (tokenFromUrl) {
        persistSession(tokenFromUrl, null, [], { authProvider: "sso" });
        const url = new URL(window.location.href);
        url.searchParams.delete("token");
        window.history.replaceState({}, "", url.toString());
      }
    }
  }, []);

  // Cargar sesión al montar
  useEffect(() => {
    isMountedRef.current = true;
    fetchSession();
    return () => { isMountedRef.current = false; };
  }, [fetchSession]);

  const handleLogout = () => {
    // Clear any local user state first
    setUser(null);
    setRaw(null);
    clearSession();

    // Force a real server-side navigation to the logout route
    if (finalUrlLogout) {
        window.location.replace(finalUrlLogout);
    }
  };

  return {
    user,
    raw,
    logout: handleLogout,
    isLoading,
    error,
    setIsLoading,
    setError,
    fetchSession,
  };
};

export const SessionContext = createContext({
  user: null,
  raw: null,
  logout: () => {},
  isLoading: false,
  setIsLoading: (_v) => {},
  setError: (_e) => {},
  error: null,
  fetchSession: async () => {},
});

export const UserSessionProvider = ({
  sessionEndpointUrl,
  urlLogout,
  userExtractor = null,
  children,
}) => {
  const session = useUserSession({ sessionEndpointUrl, urlLogout, userExtractor });
  return (
    <SessionContext.Provider value={session}>
      {children}
    </SessionContext.Provider>
  );
};
